import React, { Fragment, useState, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { List, Map } from 'immutable'

import { COLOR_OPTIONS_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'

import { wrappedPostFetch } from '@aytm/app/stats/page/sidebar/color-options/lib/func'
import { DEFAULT_PALETTE } from '@aytm/app/stats/page/sidebar/color-options/lib/constants'

// components
import { RadioButton } from 'primereact/radiobutton'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

import ColorThemes from '@aytm/app/stats/page/sidebar/color-options/ColorThemes'
import ColorPicker from '@aytm/app/stats/page/sidebar/color-options/ColorPicker'

// styles
import style from '@aytm/app/stats/page/sidebar/color-options/styles/ColorOptionsPanel.module.scss'

const ColorOptions = ({
  container,
  root,
  colorThemes,
  reportColorTheme,
  palettes,
  reportPalette,
  palettesUrl,
  isAdmin,
  isSurveyOwner,
  isSurveyGuest,
  disabled,
  onSelect,
  onUpdate,
  onError
}) => {
  const [currentStatus, setCurrentStatus] = useState(new Map())
  const [currentPalette, setCurrentPalette] = useState(null)
  const [currentPaletteId, setCurrentPaletteId] = useState(null)

  const reportPaletteId = useMemo(() => {
    const palette =
      (null !== reportPalette && palettes.find(palette => reportPalette.equals(palette.get('data')))) || null
    return null !== palette ? palette.get('id') : null
  }, [palettes, reportPalette])

  const paletteListAvailable = useMemo(
    () => 0 < palettes.size || isAdmin || isSurveyOwner,
    [palettes, isAdmin, isSurveyOwner]
  )
  const editAvailable = useMemo(() => !disabled && (isAdmin || isSurveyOwner), [disabled, isAdmin, isSurveyOwner])
  const addAvailable = useMemo(
    () => !disabled && (isAdmin || isSurveyOwner) && 7 > palettes.size && null === currentPaletteId,
    [disabled, palettes, isAdmin, isSurveyOwner, currentPaletteId]
  )
  const saveAvailable = useMemo(
    () =>
      (isAdmin || isSurveyOwner) &&
      null !== currentPalette &&
      !colorThemes.some(colorTheme => currentPalette.get('colors').equals(colorTheme.slice(0, 7))) &&
      !palettes.some(palette => currentPalette.equals(palette.get('data'))),
    [colorThemes, palettes, isAdmin, isSurveyOwner, currentPalette]
  )

  const updateCurrentStatus = (status, type, id, value) => {
    setCurrentStatus(null === value ? status.deleteIn([type, id]) : status.setIn([type, id], value))
  }

  const fetchSaveData = useCallback(() => {
    updateCurrentStatus(currentStatus, 'update', currentPaletteId, 'update')
    wrappedPostFetch(palettesUrl, { data: currentPalette.toJS() })
      .then(data => {
        updateCurrentStatus(currentStatus, 'update', currentPaletteId, null)
        switch (data.status) {
          case 'OK': {
            onUpdate(data.palettes)

            setCurrentPalette(null)
            setCurrentPaletteId(null)

            break
          }
          default: {
            onError(data.palettes)
            break
          }
        }
      })
      .catch(() => {
        updateCurrentStatus(currentStatus, 'update', currentPaletteId, null)
        onError()
      })
  }, [palettesUrl, currentStatus, currentPalette, currentPaletteId, onUpdate, onError])
  const fetchUpdateData = useCallback(() => {
    updateCurrentStatus(currentStatus, 'update', currentPaletteId, 'update')
    wrappedPostFetch(`${palettesUrl}/${currentPaletteId}/update`, { data: currentPalette.toJS() })
      .then(data => {
        updateCurrentStatus(currentStatus, 'update', currentPaletteId, null)
        switch (data.status) {
          case 'OK': {
            if (currentPaletteId == reportPaletteId) {
              onSelect(null, currentPalette)
            }

            onUpdate(data.palettes)

            setCurrentPalette(null)
            setCurrentPaletteId(null)

            break
          }
          default: {
            onError(data.palettes)
            break
          }
        }
      })
      .catch(() => {
        updateCurrentStatus(currentStatus, 'update', currentPaletteId, null)
        onError()
      })
  }, [palettesUrl, currentStatus, currentPalette, currentPaletteId, reportPaletteId, onSelect, onUpdate, onError])
  const fetchRemoveData = useCallback(
    paletteId => {
      updateCurrentStatus(currentStatus, 'remove', paletteId, 'remove')
      wrappedPostFetch(`${palettesUrl}/${paletteId}/remove`)
        .then(data => {
          updateCurrentStatus(currentStatus, 'remove', paletteId, null)
          switch (data.status) {
            case 'OK': {
              onUpdate(data.palettes)

              if (paletteId == currentPaletteId) {
                setCurrentPalette(null)
                setCurrentPaletteId(null)
              }

              break
            }
            default: {
              onError(data.palettes)
              break
            }
          }
        })
        .catch(() => {
          updateCurrentStatus(currentStatus, 'remove', paletteId, null)
          onError()
        })
    },
    [palettesUrl, currentStatus, currentPaletteId, onUpdate, onError]
  )

  const onPaletteAdd = value => {
    setCurrentPalette(value || DEFAULT_PALETTE)
    setCurrentPaletteId(-1)
  }
  const onPaletteEdit = value => {
    setCurrentPalette(value.get('data'))
    setCurrentPaletteId(value.get('id'))
  }
  const onPaletteChange = value => {
    setCurrentPalette(currentPalette.set('colors', value))
  }
  const onPaletteSave = value => {
    if (0 < value) fetchUpdateData()
    else fetchSaveData()
  }
  const onPaletteRemove = value => {
    fetchRemoveData(value)
  }
  const onPaletteCancel = () => {
    setCurrentPalette(null)
    setCurrentPaletteId(null)
  }

  useEffect(() => {
    if (
      disabled ||
      (null !== currentPaletteId &&
        0 < currentPaletteId &&
        !palettes.some(palette => currentPaletteId == palette.get('id')))
    ) {
      setCurrentPalette(null)
      setCurrentPaletteId(null)
    }
  }, [disabled, palettes, currentPaletteId])

  return (
    <div className={style.component}>
      <div className={style['component-title']}>
        Color Options
        <SidebarPopup id="color" root={root} help={COLOR_OPTIONS_HELP} width="300px" />
      </div>

      <ColorThemes
        colorThemes={colorThemes}
        reportColorTheme={reportColorTheme}
        palettes={palettes}
        reportPalette={reportPalette}
        isAdmin={isAdmin}
        isSurveyOwner={isSurveyOwner}
        isSurveyGuest={isSurveyGuest}
        disabled={disabled}
        onSelect={onSelect}
        onPaletteAdd={onPaletteAdd}
      />

      {paletteListAvailable ? (
        <>
          <div className={style['component-title']}>Custom Colors</div>

          <div className={style['palettes-list']}>
            {palettes.map(palette => (
              <Fragment key={`palette-${palette.get('id')}`}>
                <div className={style['palettes-list-item']}>
                  <RadioButton
                    checked={reportPaletteId == palette.get('id')}
                    disabled={disabled}
                    onChange={disabled ? null : () => onSelect(null, palette.get('data'))}
                  />
                  <div
                    className={classNames(style['palettes-list-item-colors'], { 'p-disabled': disabled })}
                    onClick={disabled ? null : () => onSelect(null, palette.get('data'))}
                  >
                    {palette.getIn(['data', 'colors']).map((color, index) => (
                      <span
                        key={`palette-${palette.get('id')}-color-${index}`}
                        className={style['palettes-list-item-colors-item']}
                        style={{ backgroundColor: color }}
                      ></span>
                    ))}
                  </div>
                  {editAvailable &&
                    ('remove' == currentStatus.getIn(['remove', palette.get('id')]) ? (
                      <span className={style['palettes-list-item-spin']}>
                        <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
                      </span>
                    ) : (
                      <>
                        <span className={style['palettes-list-item-edit']} onClick={() => onPaletteEdit(palette)}>
                          edit
                        </span>
                        <span
                          className={style['palettes-list-item-delete']}
                          onClick={() => updateCurrentStatus(currentStatus, 'remove', palette.get('id'), 'confirm')}
                        >
                          <i className="icon-aytmfd-delete" />
                        </span>
                      </>
                    ))}
                </div>
                {'confirm' == currentStatus.getIn(['remove', palette.get('id')]) && (
                  <div className={style['palettes-list-item-confirm']}>
                    <span
                      className={style['palettes-list-item-confirm-yes']}
                      onClick={() => onPaletteRemove(palette.get('id'))}
                    >
                      delete forever?
                    </span>
                    <span
                      className={style['palettes-list-item-confirm-no']}
                      onClick={() => updateCurrentStatus(currentStatus, 'remove', palette.get('id'), null)}
                    >
                      nevermind
                    </span>
                  </div>
                )}
              </Fragment>
            ))}

            {addAvailable && (
              <div className={style['palettes-list-add']}>
                <span className={style['palettes-list-add-button']} onClick={() => onPaletteAdd(null)}>
                  add new
                </span>
              </div>
            )}

            {null !== currentPalette && (
              <div className={style['palettes-list-picker']}>
                <span className={style['palettes-list-picker-cancel']} onClick={onPaletteCancel}>
                  <i className="icon-aytmfd-delete" />
                </span>
                <ColorPicker container={container} palette={currentPalette} onChange={onPaletteChange} />
                {'update' == currentStatus.getIn(['update', currentPaletteId]) ? (
                  <span className={style['palettes-list-picker-spin']}>
                    <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
                  </span>
                ) : (
                  <span
                    className={`${style['palettes-list-picker-save']}${
                      saveAvailable ? '' : ' ' + style['palettes-list-picker-save-disabled']
                    }`}
                    onClick={saveAvailable ? () => onPaletteSave(currentPaletteId) : null}
                  >
                    {0 < currentPaletteId ? 'update' : 'save'}
                  </span>
                )}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}

ColorOptions.propTypes = {
  container: PropTypes.any,
  root: PropTypes.any,
  colorThemes: PropTypes.instanceOf(List),
  reportColorTheme: PropTypes.number,
  palettes: PropTypes.instanceOf(List),
  defaultPalette: PropTypes.instanceOf(Map),
  reportPalette: PropTypes.instanceOf(Map),
  palettesUrl: PropTypes.string,
  isAdmin: PropTypes.bool,
  isSurveyOwner: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
  onUpdate: PropTypes.func,
  onError: PropTypes.func
}

ColorOptions.displayName = 'ColorOptions'

export default ColorOptions
